import React, { useEffect } from 'react';
import { SpotlightTabsImage } from '.';
import { useViewport } from '~/utils/viewport-provider';
import { VIEWPORT } from '~/utils/constants';
import { preloadImage } from '~/utils/helpers';

const SpotlightTabs: React.FC<SpotlightTabsProps> = ({
  spotlight_tabs,
  spotlight_style,
}) => {
  const { viewport } = useViewport();
  const [selectedTab, setSelectedTab] = React.useState(0);
  const activeRay = `z-0 before:-z-10 before:opacity-60 before:absolute before:rotate-[320deg] before:skew-y-6 before:-top-[10rem] before:-left-[4rem] before:block before:content-[''] before:w-[20rem] before:h-full before:orange-ray-gradient before:duration-200 before:pointer-events-none before:touch-action-none hover:before:scale-110 before:transform-gpu`;

  const handleTabClick = (index: number) => () => {
    if (index === selectedTab) return; // Prevent switching if already loading or same tab

    setSelectedTab(index);
  };

  useEffect(() => {
    // Preload all images
    spotlight_tabs.forEach(({ spotlight_image }, index) => {
      if (index > 0 && spotlight_image) {
        preloadImage(spotlight_image.src);
      }
    });
  }, []);

  if (!spotlight_tabs.length) return <p>Loading ...</p>;

  return (
    <div className='flex flex-col items-center gap-8'>
      <div className='flex max-w-xl justify-center gap-8 xl:max-w-lg lg:px-8 md:flex-col sm:gap-4 sm:px-4'>
        {spotlight_tabs.map((tab, index) => {
          const isActive = index === selectedTab;
          return (
            <div
              key={index}
              onClick={handleTabClick(index)}
              className={`relative flex max-w-[42rem] cursor-pointer flex-col gap-4 overflow-hidden border border-solid p-8 md:w-full md:items-start md:gap-8 ${spotlight_style?.border_radius} ${isActive ? activeRay : ''}`}
              style={{
                background: isActive
                  ? spotlight_style?.background_selected
                  : spotlight_style?.background,
                borderColor: isActive
                  ? spotlight_style?.border_selected
                  : spotlight_style?.border,
              }}
              data-active={isActive}
            >
              {tab.spotlight_badge && (
                <div
                  className='absolute right-4 top-4 px-4 py-2 text-[1.2rem] font-semibold border-rounded-strong'
                  style={{
                    color: tab.spotlight_badge.text_color,
                    background: tab.spotlight_badge.background_color,
                  }}
                >
                  {tab.spotlight_badge.text}
                </div>
              )}
              {/* Until TinaCMS figures out templates with "list: false", I have to do spotlight_title[0] */}
              {tab.spotlight_title[0]._template ===
                'simple_spotlight_title' && (
                <SimpleSpotlightTitle
                  {...(tab.spotlight_title[0] as SimpleSpotlightTitle)}
                />
              )}
              {/* Until TinaCMS figures out templates with "list: false", I have to do spotlight_title[0] */}
              {tab.spotlight_title[0]._template ===
                'multipart_spotlight_title' && (
                <MultipartSpotlightTitle
                  {...(tab.spotlight_title[0] as MultipartSpotlightTitle)}
                />
              )}
              <p
                className='text-[2.125rem]'
                style={{
                  color: tab.spotlight_text?.color,
                  fontSize: `${tab.spotlight_text?.size}rem`,
                }}
              >
                {tab.spotlight_text?.text}
              </p>
              {viewport.device === VIEWPORT.mobile && (
                <SpotlightTabsImage {...tab.spotlight_image} />
              )}
            </div>
          );
        })}
      </div>
      {['desktop', 'tablet'].includes(viewport.device) && (
        <SpotlightTabsImage {...spotlight_tabs[selectedTab]?.spotlight_image} />
      )}
    </div>
  );
};

const SimpleSpotlightTitle: React.FC<SimpleSpotlightTitle> = ({
  text,
  color,
  size,
}) => (
  <h3
    style={{
      color,
      fontSize: `${size}rem`,
    }}
    className='m-0 text-[4rem] leading-none text-text-primary'
  >
    {text}
  </h3>
);

const MultipartSpotlightTitle: React.FC<MultipartSpotlightTitle> = ({
  leading_part,
  supporting_text,
  supporting_subtext,
  color,
  leading_size,
  supporting_size,
  subtext_size,
}) => (
  <div className='flex items-center gap-2'>
    <h3
      className='m-0 text-[4rem] leading-none text-text-primary'
      style={{
        color,
        fontSize: `${leading_size}rem`,
      }}
      dangerouslySetInnerHTML={{ __html: leading_part }}
    />
    <p className='flex flex-col gap-1'>
      <span
        className='text-[1.5rem] font-bold leading-none text-text-primary'
        style={{
          color,
          fontSize: `${supporting_size}rem`,
        }}
        dangerouslySetInnerHTML={{ __html: supporting_text }}
      />
      <span
        className='text-[1.5rem] leading-none text-text-primary'
        style={{
          color,
          fontSize: `${subtext_size}rem`,
        }}
        dangerouslySetInnerHTML={{ __html: supporting_subtext }}
      />
    </p>
  </div>
);

export default SpotlightTabs;
